import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, take } from 'rxjs';
import { APIResponse } from 'fakturnia-shared';
import { ApiService } from 'shared/services/api.service';
import { Utils } from 'fakturnia-shared';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TextFieldModule } from '@angular/cdk/text-field';
import { SnackbarService } from 'shared/services/snackbar.service';

@Component({
  standalone: true,
  imports: [
    CommonModule, 
    MatFormFieldModule, 
    MatInputModule, 
    ReactiveFormsModule, 
    MatSelectModule, 
    MatButtonModule, 
    MatIconModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    TextFieldModule
  ],
  selector: 'app-create-or-update-announcement',
  templateUrl: './create-or-update-announcement.component.html',
  styleUrls: ['./create-or-update-announcement.component.scss']
})
export class CreateOrUpdateAnnouncementComponent implements OnDestroy {
  
  mode: 'create' | 'update' = 'create';
  private _subscriptions: Subscription[] = [];
  id: string | null = null;
  isLoading = false;
  isSaving = false;
  isDeleting = false;

  form = new FormGroup({
    title: new FormControl('', [Validators.required, Validators.minLength(3)]),
    description: new FormControl('', [Validators.required, Validators.minLength(10)]),
    isActive: new FormControl(false),
  });

  constructor(
    private _apiService: ApiService,
    private _route: ActivatedRoute,
    private _router: Router,
    private _snackbarService: SnackbarService
  ) {
    this._subscriptions.push(
      this._route.params.subscribe((data: any) => {
        if (data != null) {
          if (!Utils.isNullOrEmpty(data.id)) {
            if (data.id === 'new') return;
            this.id = data.id;
            this.mode = 'update';
            this._getAnnouncement(data.id);
          }
        }
      })
    );
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe());
  }

  save() {
    if (this.form.invalid) {
      this._markFormGroupTouched(this.form);
      this._snackbarService.showSnackBar('Proszę poprawić błędy w formularzu', 'error', 3000);
      return;
    }

    this.isSaving = true;
    this._apiService.updateAnnouncement(this.id, this.form.value).pipe(take(1)).subscribe({
      next: (response: APIResponse) => {
        this.isSaving = false;
        if (response.success) {
          this._snackbarService.showSnackBar('Ogłoszenie zostało zaktualizowane', 'success', 3000);
          this._router.navigateByUrl('/admin/announcements');
        }
      },
      error: () => {
        this.isSaving = false;
        this._snackbarService.showSnackBar('Błąd podczas aktualizacji ogłoszenia', 'error', 3000);
      }
    });
  }

  create() {
    if (this.form.invalid) {
      this._markFormGroupTouched(this.form);
      this._snackbarService.showSnackBar('Proszę poprawić błędy w formularzu', 'error', 3000);
      return;
    }

    this.isSaving = true;
    this._apiService.createAnnouncement(this.form.value).pipe(take(1)).subscribe({
      next: (response: APIResponse) => {
        this.isSaving = false;
        if (response.success) {
          this._snackbarService.showSnackBar('Ogłoszenie zostało utworzone', 'success', 3000);
          this._router.navigateByUrl('/admin/announcements');
        }
      },
      error: () => {
        this.isSaving = false;
        this._snackbarService.showSnackBar('Błąd podczas tworzenia ogłoszenia', 'error', 3000);
      }
    });
  }
  
  remove() {
    if (!confirm('Czy na pewno chcesz usunąć to ogłoszenie?')) {
      return;
    }
    this.delete();
  }

  delete() {
    this.isDeleting = true;
    this._apiService.deleteAnnouncement(this.id).pipe(take(1)).subscribe({
      next: (response: APIResponse) => {
        this.isDeleting = false;
        if (response.success) {
          this._snackbarService.showSnackBar('Ogłoszenie zostało usunięte', 'success', 3000);
          this._router.navigateByUrl('/admin/announcements');
        }
      },
      error: () => {
        this.isDeleting = false;
        this._snackbarService.showSnackBar('Błąd podczas usuwania ogłoszenia', 'error', 3000);
      }
    });
  }

  private _getAnnouncement(id: string) {
    this.isLoading = true;
    this._apiService.getAnnouncementAsAdmin(id).pipe(take(1)).subscribe({
      next: (response: APIResponse) => {
        this.isLoading = false;
        if (response.success) {
          this.form.patchValue({
            title: response.data.title,
            description: response.data.description,
            isActive: response.data.isActive
          });
        }
      },
      error: () => {
        this.isLoading = false;
        this._snackbarService.showSnackBar('Błąd podczas pobierania ogłoszenia', 'error', 3000);
      }
    });
  }

  goBack() {
    this._router.navigateByUrl('/admin/announcements');
  }

  private _markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this._markFormGroupTouched(control);
      }
    });
  }
}
